import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';
import { toast } from 'sonner';
import { CameraOffIcon, PrinterIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, IconButton } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePatient, useDeletePatient } from '@hooks/usePatient';
import { useJawsScanSummariesByPatient, useDeleteJawsScan } from '@hooks/useJawsScan';
import { useIsAdmin } from '@/hooks/useAdmin';
import { usePatientDetailStore } from '@patientMenu/hooks/usePatientDetailStore';
import PatientScanList from '@patientMenu/patientDetail/PatientScanList';
import { scanWorkflows } from '@patientMenu/patientDetail/scanWorkflows';
import NotesPanel from '@patientMenu/patientDetail/patientScan/NotesPanel';
import TeethPreview from '@patientMenu/patientDetail/patientScan/TeethPreview';
import { cn } from '@/lib/utils';
import { Status } from '@/schema/jawsScan';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PrintModelsDialog } from './PrintModelsDialog';
import { formatDate } from '../lib/format';

interface AdminCasePanelProps {
  patientId: string;
  scanId: string | null;
  onSelectScan: (scanId: string) => void;
  onClose: () => void;
  className?: string;
}

/**
 * The console's own read of a case: the same overview the clinic sees next to its patient
 * table. What it offers depends on who is looking — an admin is a platform viewer and gets
 * the print handover and nothing else, while a superadmin can open the case in the clinical
 * tools or delete it outright.
 */
export function AdminCasePanel({ patientId, scanId, onSelectScan, onClose, className }: AdminCasePanelProps) {
  const navigate = useNavigate();
  const [printOpen, setPrintOpen] = useState(false);
  const [confirm, setConfirm] = useState<'scan' | 'patient' | null>(null);
  const { selectedScan, setSelectedScan } = usePatientDetailStore();
  const { isSuperAdmin } = useIsAdmin();

  const deleteScan = useDeleteJawsScan();
  // Staying put on success: the console has no clinic list to be sent back to.
  const deletePatient = useDeletePatient({ redirectTo: null });

  const patientQuery = usePatient(patientId);
  const scansQuery = useJawsScanSummariesByPatient(patientId);

  const sortedScans = useMemo(
    () =>
      [...(scansQuery.data ?? [])].sort(
        (a, b) => new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime(),
      ),
    [scansQuery.data],
  );

  // The URL owns which scan is open; this only mirrors it into the store the shared
  // preview/notes panels read from, and fills the blank with the newest scan.
  useEffect(() => {
    if (!scansQuery.data) return;

    if (sortedScans.length === 0) {
      if (selectedScan !== null) setSelectedScan(null);
      return;
    }

    const matched = sortedScans.find((scan) => scan.id === scanId);
    if (!matched) {
      onSelectScan(sortedScans[0].id);
      return;
    }

    if (selectedScan?.id !== matched.id) setSelectedScan(matched);
  }, [scanId, scansQuery.data, sortedScans, selectedScan, setSelectedScan, onSelectScan]);

  // Leaving a stale scan in the shared store would show the next opened case someone
  // else's teeth for a frame.
  useEffect(() => () => setSelectedScan(null), [setSelectedScan]);

  const patient = patientQuery.data;
  const fullName = patient
    ? [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ')
    : '';
  const age = patient?.dateOfBirth ? differenceInYears(new Date(), new Date(patient.dateOfBirth)) : null;

  const isLoading = patientQuery.isLoading || scansQuery.isLoading;
  const loadError = patientQuery.error ?? scansQuery.error;
  const workflows = scanWorkflows(patientId, selectedScan);

  const handleDeleteScan = () => {
    if (!selectedScan) return;
    deleteScan.mutate(
      { scanId: selectedScan.id, patientId },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success('Scan deleted');
          const next = sortedScans.find((scan) => scan.id !== selectedScan.id);
          setSelectedScan(null);
          if (next) onSelectScan(next.id);
        },
      },
    );
  };

  const handleDeletePatient = () => {
    deletePatient.mutate(patientId, {
      onSuccess: () => {
        setConfirm(null);
        toast.success('Patient deleted');
        // Nothing left to show, so the panel closes itself.
        onClose();
      },
    });
  };

  const stats = [
    { label: 'Total scans', value: String(sortedScans.length) },
    { label: 'Latest scan', value: formatDate(sortedScans[0]?.scanDate) },
    { label: 'Current scan', value: formatDate(selectedScan?.scanDate) },
  ];

  return (
    <aside className={cn('surface-panel flex min-h-0 flex-col overflow-hidden', className)}>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/6 px-3.5 py-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">Patient overview</p>
        <IconButton
          onClick={onClose}
          variant="ghost-light"
          size="icon"
          className="h-8 w-8 rounded-lg"
          icon={XIcon}
          aria-label="Close case detail"
        />
      </header>

      <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3.5 text-white">
        {isLoading && <p className="text-sm text-white/60">Loading the case…</p>}

        {!isLoading && loadError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5">
            <p className="text-sm font-semibold text-red-200">Could not load this case</p>
            <p className="text-xs text-red-200/70">{loadError.message}</p>
          </div>
        )}

        {!isLoading && !loadError && patient && (
          <>
            <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1.5">
                <h2 className="truncate text-xl font-semibold tracking-tight">{fullName}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
                  <span>Clinic: {patient.clinicName}</span>
                  <span>Orthodontist: {patient.orthodontistName}</span>
                  {age !== null && <span>Age: {age}</span>}
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {selectedScan && (
                  <Button
                    size="sm"
                    variant={selectedScan.status === Status.printing ? 'ghost-light' : 'ghost-primary'}
                    onClick={() => setPrintOpen(true)}
                  >
                    <PrinterIcon />
                    {selectedScan.status === Status.printing ? 'Printing' : 'Print'}
                  </Button>
                )}

                {/* A plain admin reads the case; a superadmin works it. */}
                {isSuperAdmin && selectedScan && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost-light">
                        Workflows
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-44" align="end">
                      {workflows.map((workflow) => (
                        <DropdownMenuItem
                          key={workflow.key}
                          className="rounded-lg"
                          variant={workflow.disabled ? 'disabled' : 'default'}
                          onClick={() => {
                            if (!workflow.disabled && workflow.path) navigate(workflow.path);
                          }}
                        >
                          {workflow.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {isSuperAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost-danger" aria-label="Delete">
                        <Trash2Icon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-44" align="end">
                      <DropdownMenuItem
                        className="rounded-lg"
                        variant={selectedScan ? 'default' : 'disabled'}
                        onClick={() => selectedScan && setConfirm('scan')}
                      >
                        Delete this scan
                      </DropdownMenuItem>
                      <DropdownMenuItem className="rounded-lg" onClick={() => setConfirm('patient')}>
                        Delete patient
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            <div className="grid shrink-0 gap-2 sm:grid-cols-3">
              {stats.map(({ label, value }) => (
                <div key={label} className="surface-tile px-3 py-3 text-sm text-white/85">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45">{label}</p>
                  <p className="mt-1 text-base font-semibold text-white">{value}</p>
                </div>
              ))}
            </div>

            {selectedScan === null ? (
              <div className="surface-tile flex flex-col items-center justify-center gap-3 border-dashed px-6 py-14 text-white/60">
                <CameraOffIcon className="h-12 w-12 text-white/30" />
                <div className="text-center">
                  <p className="text-sm font-semibold text-white/85">No scans yet</p>
                  <p className="mt-0.5 text-xs text-white/50">This patient has nothing imported.</p>
                </div>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <div className="shrink-0">
                  <StatusBadge status={selectedScan.status} />
                </div>
                <TeethPreview className="h-72 shrink-0" />
                <PatientScanList
                  className="h-64 shrink-0"
                  scansSummary={sortedScans}
                  readOnly
                  onSelect={(scan) => onSelectScan(scan.id)}
                />
                <NotesPanel className="min-h-[7rem] shrink-0" readOnly />
              </div>
            )}
          </>
        )}
      </div>

      {confirm === 'scan' && selectedScan && (
        <ConfirmDialog
          title="Delete scan"
          confirmLabel="Delete scan"
          onConfirm={handleDeleteScan}
          onClose={() => setConfirm(null)}
          isPending={deleteScan.isPending}
          error={deleteScan.error?.message}
        >
          <p>
            The scan from {formatDate(selectedScan.scanDate)} for <strong>{fullName}</strong> and
            everything built on it — segmentation, corrections, braces — is removed for good.
          </p>
          <p className="text-white/50">The clinic is not notified.</p>
        </ConfirmDialog>
      )}

      {confirm === 'patient' && (
        <ConfirmDialog
          title="Delete patient"
          confirmLabel="Delete patient"
          onConfirm={handleDeletePatient}
          onClose={() => setConfirm(null)}
          isPending={deletePatient.isPending}
          error={deletePatient.error?.message}
        >
          <p>
            <strong>{fullName}</strong> and all {sortedScans.length} scan
            {sortedScans.length === 1 ? '' : 's'} of theirs are removed for good.
          </p>
          <p className="text-white/50">The clinic is not notified.</p>
        </ConfirmDialog>
      )}

      {printOpen && selectedScan && (
        <PrintModelsDialog
          row={{
            scanId: selectedScan.id,
            patientName: fullName,
            clinicName: patient?.clinicName,
            status: selectedScan.status,
          }}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </aside>
  );
}

export default AdminCasePanel;
